/*eslint no-constant-condition: "off"*/
import React, { createRef } from 'react';
import moment from 'moment-jalaali';
import {
  isANumber,
  convertDigitToProperFormat,
  convertNumbersToProperFormat,
} from 'utils/number';
import {
  Badge,
  Cell,
  HeaderColumn,
  HeaderColumnNoStick,
  HeaderColumnTitle,
  HeaderRow,
  BottomActions,
} from './styles';
import messages from './Messages';
import { connect, MapDispatchToProps } from 'react-redux';
import { blue, generatePercentageColor } from '../../utils/style/colors';
import { IQueryState, MapRequest, MapResponse } from '../Chart/ChartViewer';
import { monthIndexToTimestamp, smartDateString } from '../../utils/date';
import APopover from 'components/APopover';
import Bucket, { getJMonthIndex, IAnswerItem, IContext } from '../Chart/Bucket';
import { ActionIcon } from '../Chart/styles';
import Icon from '../Icon';
import { OpenItemsContext } from 'store/context/OpenItemsContext';
import { printElement } from '../../utils/happyFunctions';
import Button from '../Button/Button';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from '../FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import { roundToDynamicFloating } from '../Chart/analyseUtils';
import Toggle from '../Toggle';
import {
  ICheckPoint,
  IIndicatorQuestionView,
  IProviderSurveyReport,
  IReportView,
  ISurveyItem,
  ISurveyReports,
} from 'store/survey/surveyTypes';
import { ISurveys } from 'store/survey/surveyTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import {
  IProviderItems,
  IProviders,
  ITags,
  ITagView,
} from 'store/providers/providerTypes';
import {
  cumulativePercentageReady,
  cumulativePercentageReadyFn,
} from 'store/survey/surveySlice';
import { appDispatch } from 'store/store';
import { getCalendarLocal } from '../../i18n';
import { getEvaluatedMetric } from '../FileManager/ItemsViewer/Survey/SurveyEditor/Identification';
import { IAnalyticResponse } from 'store/entity/IAnalyticResponse';

interface IDispatchToProps {
  cumulativePercentageReady: (props: {
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  }) => ReturnType<typeof cumulativePercentageReadyFn>;
}

type IOwnProps = {
  mapRequest: MapRequest | undefined;
  mapResponse: MapResponse | undefined;
  reports: ISurveyReports['data'];
  surveys: ISurveys;
  isPrinting: boolean;
  reportItem?: IOpenItem;
  reportItemMap: {
    [key: number]: { itemId: number; reportToken: string };
  };
  providerItems: IProviderItems['items'];
  tags: ITags;
  surveyItem?: ISurveyItem;
  providers?: IProviders;
  providerId: number;
  // onClickOnCell: (itemId: number) => void;
  // groupId?: string;
  // itemKey?: string;
  // series: string[];
  // showPercentage: boolean;
  // reverse?: boolean;
};
type ComponentProps = IOwnProps & IDispatchToProps;
type Node = {
  tag?: ITagView | undefined;
  reports: {
    id: number;
    request: {
      data: IQueryState;
      order: number;
    };
    noAccess: boolean;
  }[];
  nodes: Node[];
  height?: number;
  childDepth?: number;
  headerTitles?: string[];
  titleSpan?: number;
  emptyCount?: number;
};

function bound(value?: number) {
  if (isANumber(value)) return Math.max(Math.min(100, value!), 0);
  return value;
}
type IState = {
  showEmptyRows: boolean;
};
class MergedChart extends React.PureComponent<ComponentProps, IState> {
  state = { showEmptyRows: false };
  render() {
    const { showEmptyRows } = this.state;
    const contentRef = createRef<HTMLTableElement>();
    const {
      reports,
      mapRequest,
      mapResponse,
      reportItem,
      providerItems,
      tags,
    } = this.props;
    let minDate: number | undefined = undefined;
    let maxDate: number | undefined = undefined;
    const groupByTagIds =
      (reportItem?.item.data as IProviderSurveyReport).groupByTags || [];
    const notViewedReportIds = {
      timeBased: [] as number[],
      tagBased: [] as number[],
    };

    Object.entries(mapResponse || {}).forEach(([id, item]) => {
      const lastIndex = Number(
        (item[0] as IAnalyticResponse)?.categories?.['TermByTime']?.buckets
          .length - 1
      );
      if (!isANumber(lastIndex)) return;
      if (
        minDate === undefined ||
        minDate >
          Number(
            (item[0] as IAnalyticResponse)?.categories['TermByTime'].buckets[0]
              .val
          )
      ) {
        minDate = Number(
          (item[0] as IAnalyticResponse)?.categories['TermByTime'].buckets[0]
            .val
        );
      }
      const currentEndDate =
        Number(
          (item[0] as IAnalyticResponse)?.categories['TermByTime'].buckets[
            lastIndex
          ].val
        ) ||
        Number(
          (item[0] as IAnalyticResponse)?.categories['TermByTime'].buckets[
            lastIndex - 1
          ].val
        ) +
          Number(mapRequest![id]?.data[0]?.buckets[0]?.gap || 1) -
          1;
      if (maxDate === undefined || maxDate < currentEndDate) {
        maxDate = currentEndDate;
      }
    });
    const partValues = new Set<string>();
    const partValueList = [] as number[];

    const columnType = minDate && maxDate ? 'date' : 'tag';
    const reportGroupRoot = this.extractGroups(
      groupByTagIds,
      tags,
      mapRequest,
      reports,
      reportItem!,
      providerItems,
      notViewedReportIds,
      mapResponse,
      columnType
    );

    if (columnType === 'tag') {
      Object.values(mapResponse || {}).forEach((item) => {
        (item[0] as IAnalyticResponse)?.categories?.[
          'TermByAnswers'
        ]?.buckets.forEach((it) => {
          const tagIdString = it.val.toString().split('$$')[1];

          if (it.val !== '__total' && !partValues.has(tagIdString)) {
            partValues.add(tagIdString);
            partValueList.push(Number(tagIdString));
          }
        });
      });
      if (partValues === null)
        return (
          <QuestionHintBox>
            <HintIcon type="info" />
            <HintText>
              <span>{messages.noDataFound}</span>
            </HintText>
          </QuestionHintBox>
        );
    }

    const finalNotViewedReportIds =
      columnType === 'date'
        ? notViewedReportIds.timeBased
        : notViewedReportIds.tagBased;
    const dates: number[] =
      columnType === 'date'
        ? Array.from(Array(maxDate! - minDate! + 1).keys()).map(
            (i) => i + minDate!
          )
        : partValueList;
    const groupNames: { [key: number]: string } = {};
    const groupOrder = [] as number[];
    const renderGroupAfter: { [key: number]: number } = {};
    if (columnType === 'date' && dates[dates.length - 1] - dates[0] > 12)
      dates.forEach((it, index) => {
        const yearIndex = Math.floor(it / 12);
        // datesGroupMap[it] = yearIndex;
        groupNames[yearIndex] = convertDigitToProperFormat(yearIndex);
        if (!groupOrder.includes(yearIndex)) {
          groupOrder.push(yearIndex);
          if (index !== 0) {
            renderGroupAfter[dates[index - 1]] = dates[index - 1] / 12;
          }
        }
        if (index === dates.length - 1) renderGroupAfter[it] = yearIndex;
      });
    else {
      const datesGroupMap: { [key: number]: number } = {};
      dates.forEach((it) => {
        const dadTag = Number(tags.data[it]?.parentId || 0);
        datesGroupMap[it] = dadTag;
        groupNames[dadTag] =
          dadTag === 0 ? messages.other : tags.data[dadTag]?.text;
        if (!groupOrder.includes(dadTag)) groupOrder.push(dadTag);
      });
      dates.sort((a, b) => {
        if (datesGroupMap[a] === datesGroupMap[b])
          return (tags.data[a]?.order || 0) - (tags.data[b]?.order || 0);
        return (
          (tags.data[datesGroupMap[a]]?.order || 0) -
          (tags.data[datesGroupMap[b]]?.order || 0)
        );
      });
      for (let i = 0; i < dates.length; i++) {
        if (
          i === dates.length - 1 ||
          datesGroupMap[dates[i]] !== datesGroupMap[dates[i + 1]]
        )
          renderGroupAfter[dates[i]] = datesGroupMap[dates[i]];
      }
    }
    const nodesRenderd = this.renderNode(
      reportGroupRoot,
      dates,
      true,
      columnType,
      renderGroupAfter
    );
    let totalPercentageCount = 0;
    let totalPercentageSum = 0;
    nodesRenderd.forEach(({ totalPercent }) => {
      if (isANumber(totalPercent)) {
        totalPercentageCount++;
        totalPercentageSum += totalPercent!;
      }
    });
    const print = () => {
      contentRef?.current && printElement(contentRef!.current!);
    };

    const local = getCalendarLocal();
    return (
      <div>
        <table
          className="highlightedTable"
          style={{ maxHeight: '100%' }}
          ref={contentRef}
        >
          <>
            <thead>
              <tr>
                {reportGroupRoot.nodes.length && reportGroupRoot.headerTitles
                  ? reportGroupRoot.headerTitles?.map((it) => {
                      return (
                        <HeaderRow
                          key={it}
                          style={{
                            zIndex: 100,
                          }}
                          bgColor={'#F2F2F2'}
                        >
                          {it}
                        </HeaderRow>
                      );
                    })
                  : undefined}
                <HeaderRow
                  bgColor={'#F2F2F2'}
                  style={{
                    zIndex: 100,
                    right: 0,
                  }}
                >
                  {messages.title}
                </HeaderRow>
                <HeaderRow
                  bgColor={'#F2F2F2'}
                  style={{
                    zIndex: 100,
                    right: 0,
                  }}
                >
                  {messages.unit}
                </HeaderRow>

                {dates.map((month) => {
                  const monthName =
                    columnType === 'date'
                      ? convertDigitToProperFormat(
                          (local === 'fa_IR@calendar=persian'
                            ? moment(monthIndexToTimestamp(month, local))
                            : moment(
                                monthIndexToTimestamp(month, local)
                              ).locale('en')
                          ).format(
                            local === 'fa_IR@calendar=persian'
                              ? 'jMMMM jYYYY'
                              : 'MMMM YYYY'
                          )
                        )
                      : tags.data?.[month]?.text;

                  const headerItem = (
                    <React.Fragment key={month}>
                      <HeaderRow>{monthName}</HeaderRow>
                    </React.Fragment>
                  );

                  if (renderGroupAfter[month]) {
                    const groupItem = (
                      <React.Fragment key={month}>
                        <HeaderRow className="sum">
                          {groupNames[renderGroupAfter[month]]}
                        </HeaderRow>
                      </React.Fragment>
                    );
                    return (
                      <>
                        {headerItem}
                        {groupItem}
                      </>
                    );
                  } else return headerItem;
                })}

                {columnType === 'date' && (
                  <>
                    <HeaderRow className="sum">{messages.target}</HeaderRow>

                    <HeaderRow
                      className="sum"
                      colSpan={reportGroupRoot.nodes.length ? 2 : 1}
                    >
                      {messages.average}
                      {totalPercentageCount ? (
                        <Badge
                          className={generatePercentageColor(
                            totalPercentageSum / totalPercentageCount,
                            undefined,
                            true
                          )}
                          style={{ borderRadius: '15px' }}
                        >
                          {'٪ ' +
                            convertNumbersToProperFormat(
                              totalPercentageSum / totalPercentageCount,
                              2
                            )}
                        </Badge>
                      ) : (
                        <></>
                      )}
                    </HeaderRow>
                  </>
                )}
              </tr>
            </thead>
            <tbody>{nodesRenderd.map((it) => it.node)}</tbody>
          </>
        </table>
        <div>
          {finalNotViewedReportIds.length ? (
            <div>
              <p>{messages.notShownReports}</p>
              {finalNotViewedReportIds.map((reportId) => {
                return <li key={reportId}>{reports[reportId].name}</li>;
              })}
            </div>
          ) : (
            <div />
          )}
        </div>
        <BottomActions>
          <Toggle
            checked={showEmptyRows}
            onChange={() => {
              this.setState({ showEmptyRows: !showEmptyRows });
            }}
            label={
              messages.showEmptyRows +
              `(${convertDigitToProperFormat(reportGroupRoot.emptyCount!)})`
            }
          />
          <Button onClick={print}>{messages.print}</Button>
        </BottomActions>
      </div>
    );
  }

  private extractGroups(
    groupByTagIds: number[],
    tags: ITags,
    mapRequest: MapRequest | undefined,
    reports: ISurveyReports['data'],
    _reportItem: IOpenItem,
    providerItems: any,
    notViewedReportIds: { timeBased: number[]; tagBased: number[] },
    mapResponse: MapResponse | undefined,
    columnType: 'tag' | 'date'
  ) {
    const reportGroupRoot: Node = groupByTagIds.length
      ? {
          tag: tags.data[groupByTagIds[0]],
          nodes: [],
          reports: [],
        }
      : {
          tag: undefined,
          reports: [],
          nodes: [],
        };
    reportGroupRoot.emptyCount = 0;
    Object.entries(mapRequest || {})
      .sort((a, b) => a[1].order - b[1].order)
      .forEach((item) => {
        const reportId = Number(item[0]);
        const report: IReportView = reports[reportId];
        if (
          !mapResponse ||
          (!(mapResponse[reportId][0] as IAnalyticResponse)?.categories &&
            !this.state.showEmptyRows)
        ) {
          reportGroupRoot.emptyCount!++;
          return;
        }
        if (!report) return;
        const reportTags = report.tags.reduce(
          (current: ITagView[], item: string) => {
            const itemTag = Object.values(tags.data).find((value) => {
              return item === value.text;
            });
            if (itemTag) current.push(itemTag);
            return current;
          },
          []
        );
        if (!report?.surveyId) return;
        const surveyItemId = this.props.reportItemMap[reportId].itemId;
        const itemTags = surveyItemId
          ? (providerItems[surveyItemId]?.data?.tags ?? null)
          : [];
        this.insertReportToTree(
          reportGroupRoot,
          [...(itemTags || []), ...(reportTags || [])],
          reportId,
          groupByTagIds,
          item[1],
          itemTags === null,
          notViewedReportIds,
          columnType
        );
      });
    this.setChildDepth(reportGroupRoot);
    this.setTitleSpan(reportGroupRoot);
    return reportGroupRoot;
  }

  // renderNode = (node: Node) => {};

  renderReport = (
    item: [string, { data: IQueryState; order: number }],
    dates: number[],
    headerSpan: number,
    noAccess: boolean,
    columnType: 'date' | 'tag',
    renderGroupAfter: { [key: number]: number }
  ) => {
    const local = getCalendarLocal();
    const { mapResponse, surveys, reports } = this.props;
    const reportId = Number(item[0]);
    const request = item[1];
    const analyse = mapResponse![reportId];
    const query = request.data[0];
    const report: IReportView = reports[Number(reportId)];
    const series = report.series;
    const checkPoints: ICheckPoint[] = Object.values(
      surveys.checkPoints.data
    ).filter((checkpoint: ICheckPoint) => {
      if (reportId) {
        return checkpoint.reportId === reportId; // a checkpoint which belongs to the report
      }
      return false;
    });
    const surveyId = reports[reportId].surveyId;
    const survey = surveys.data[surveyId!];
    const surveyVersion = survey.viewingVersion || survey.data.currentVersion;
    const questions = survey.data.versions[surveyVersion].questionMap!;
    const context: IContext = {
      tags: this.props.tags,
      questions,
      timeScale: report.time.timeScale,
      checkPoints,
      fields: [],
      local: this.props.surveyItem?.providerId
        ? this.props.providers?.map?.[this.props.surveyItem?.providerId]?.data
            ?.surveyOwnerProperties?.localId || local
        : local,
    };
    let unit = '';
    if (
      series[0] &&
      series[0]?.type === 'question' &&
      (series[0].questionId || series[0].questionIds?.length === 1)
    ) {
      const questionId = (series[0].questionId ||
        series[0].questionIds?.[0]) as number;
      if (questionId && questions?.[questionId]?.type === 'indicator') {
        if (!series[0].subQuestionId) {
          unit = getEvaluatedMetric(
            (questions?.[questionId] as IIndicatorQuestionView)
              ?.numeratorUnit as string,
            (questions?.[questionId] as IIndicatorQuestionView)
              ?.denominatorUnit as string
          );
        } else
          unit = (questions?.[questionId] as any)?.[series?.[0]?.subQuestionId];
      }
    }

    const ans = Bucket.bucketOf(
      (query.buckets || [])[0],
      (query.metrics || [])[0],
      query.filters,
      analyse?.[0] as IAnalyticResponse,
      series[0],
      context
    ).extractResponse((analyse?.[0] as IAnalyticResponse)?.categories);
    const targetInfo = context.checkPoints
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0))
      .reduce(
        (
          current:
            | {
                time: number;
                target: number;
                baseLine: number;
                jMonthIndex: number;
              }
            | undefined,
          item
        ) => {
          if (columnType !== 'date') return current;
          const jMonthIndex = getJMonthIndex(new Date(item.date!), local);
          if (current === undefined) {
            if (jMonthIndex >= dates[0])
              return {
                time: item.date!,
                target: item.target!,
                baseLine: item.baseLine!,
                jMonthIndex,
              };
            else return { time: 0, target: 0, baseLine: 0, jMonthIndex };
          }

          if (jMonthIndex >= dates[dates.length - 1]) {
            return {
              time: item.date!,
              target: item.target!,
              baseLine: item.baseLine!,
              jMonthIndex,
            };
          }
          return current;
        },
        undefined
      ) || { time: 0, target: 0, baseLine: 0, jMonthIndex: 0 };
    let count = 0;
    let valueCount = 0;
    let sum = 0;
    let valueSum = 0;
    ans.forEach((it) => {
      if (isANumber(it.__id)) {
        if (isANumber(it.__percentageOfRealization)) {
          const weight = it.__value__d || 1;
          sum += Number(it.__percentageOfRealization) * weight;
          count++;
        }
        if (isANumber(it.__value)) {
          if (isANumber(it.__value__n)) {
            valueSum += Number(it.__value__n);
            valueCount += Number(it.__value__d) || 1;
          } else {
            valueSum += Number(it.__value);
            valueCount += 1;
          }
        }
      }
    });
    const totalPercent = count > 0 ? sum / count : undefined;
    const totalValue = valueSum / valueCount;
    let colSpan = 1;
    if (columnType === 'date')
      switch (report.time.timeScale) {
        case 'day':
          colSpan = 1;
          break;
        case 'week':
          colSpan = 1;
          break;
        case 'month':
          colSpan = 1;
          break;
        case 'season':
          colSpan = 3;
          break;
        case 'twoSeason':
          colSpan = 6;
          break;
        case 'year':
          colSpan = 12;
          break;
      }
    let localPercentSum = 0;
    let localValueSum = 0;
    let localPercentCount = 0;
    let localCount = 0;

    const totalColor = totalPercent
      ? generatePercentageColor(totalPercent!, undefined, true)
      : '';
    const pItem = this.props.reportItemMap[reportId].itemId;
    return {
      node: (
        <>
          <APopover
            trigger="click"
            curveBorder
            arrow
            placement="left"
            target={
              <HeaderColumn colSpan={headerSpan} style={{ cursor: 'pointer' }}>
                <HeaderColumnTitle>
                  {noAccess ? '🔒' : ''}
                  {report.name}
                </HeaderColumnTitle>
              </HeaderColumn>
            }
          >
            <div>
              {noAccess ? (
                <span />
              ) : (
                <OpenItemsContext.Consumer>
                  {(context) => (
                    <ActionIcon
                      onClick={() => {
                        if (item)
                          context.changeOpenItems(pItem, this.props.providerId);
                      }}
                    >
                      <Icon
                        type="link"
                        color={blue}
                        style={{
                          margin: '0 0 0 1em',
                        }}
                      />
                      <span style={{ whiteSpace: 'pre-wrap' }}>
                        {this.props.providerItems?.[pItem || 0]?.data?.name}
                      </span>
                    </ActionIcon>
                  )}
                </OpenItemsContext.Consumer>
              )}
            </div>
          </APopover>
          <HeaderColumnNoStick style={{ fontSize: '15px' }} colSpan={1}>
            {unit || '---'}
          </HeaderColumnNoStick>
          {dates
            .filter((_: any, index: number) => index % colSpan === 0)
            .map((month: number) => {
              const cat = ans.find((it) => {
                return it['__id'] === month;
              });
              let returnedCell: JSX.Element;
              if (!cat) {
                returnedCell = (
                  <Cell colSpan={colSpan} key={month} className={'mc_cell'}>
                    <span>{messages.withoutData}</span>
                  </Cell>
                );
              } else {
                const value = cat['__value'];
                const percentage = cat['__percentageOfRealization'];
                const baseLine = cat['__baseLine'];
                const target = cat['__target'];
                if (isANumber(value)) {
                  localValueSum +=
                    cat['__value__n'] || (cat['__value'] as number) || 0;
                  localCount += cat['__value__d'] || 1;
                }
                if (isANumber(cat['__percentageOfRealization'])) {
                  localPercentSum +=
                    Number(cat['__percentageOfRealization']) *
                    (cat['__value__d'] || 1);
                  localPercentCount += cat['__value__d'] || 1;
                }
                returnedCell = this.getReturnedCell(
                  month,
                  colSpan,
                  percentage,
                  baseLine,
                  target,
                  value
                );
              }
              if (
                renderGroupAfter[month] ||
                (columnType === 'date' && renderGroupAfter[month + colSpan - 1])
              ) {
                const groupCell = this.getReturnedCell(
                  renderGroupAfter[month] || 0,
                  1,
                  localPercentCount > 0
                    ? roundToDynamicFloating(
                        localPercentSum / localPercentCount
                      )
                    : undefined,
                  undefined,
                  undefined,
                  ['sum', 'count'].includes(query.metrics[0].metric)
                    ? roundToDynamicFloating(localValueSum)
                    : localCount > 0
                      ? roundToDynamicFloating(localValueSum / localCount)
                      : undefined,
                  true
                );
                localPercentSum = 0;
                localValueSum = 0;
                localPercentCount = 0;
                localCount = 0;
                return (
                  <>
                    {returnedCell}
                    {groupCell}
                  </>
                );
              }
              return returnedCell;
            })}
          {columnType === 'date' &&
            (targetInfo.time ? (
              <>
                <APopover
                  trigger="hover"
                  arrow
                  curveBorder
                  target={
                    <HeaderColumnNoStick colSpan={1}>
                      {convertNumbersToProperFormat(targetInfo.target, 2)}
                    </HeaderColumnNoStick>
                  }
                >
                  <div>
                    <div>{smartDateString(targetInfo.time, false, true)}</div>
                    <span>
                      {messages.initialValue +
                        ': ' +
                        convertDigitToProperFormat(targetInfo.baseLine)}
                    </span>
                  </div>
                </APopover>
              </>
            ) : (
              <>
                <HeaderColumnNoStick>
                  {messages.withoutData}
                </HeaderColumnNoStick>
              </>
            ))}
          {columnType === 'date' && (
            <HeaderColumnNoStick
              style={{ fontSize: '15px' }}
              colSpan={1}
              className={targetInfo.time ? totalColor : undefined}
            >
              {isANumber(totalValue)
                ? convertNumbersToProperFormat(totalValue, 2)
                : messages.withoutData}
              {targetInfo.time ? (
                <Badge className={totalColor + ' mc_badgeCell'}>
                  {isANumber(totalPercent)
                    ? '٪ ' +
                      convertNumbersToProperFormat(bound(totalPercent!)!, 2)
                    : messages.withoutData}
                </Badge>
              ) : (
                <></>
              )}
            </HeaderColumnNoStick>
          )}
        </>
      ),
      totalPercent: bound(totalPercent),
    };
  };

  private getReturnedCell(
    month: number,
    colSpan: number,
    percentage: number | undefined,
    _baseLine: number | undefined,
    _target: number | undefined,
    value: number | IAnswerItem[] | undefined,
    header?: boolean
  ) {
    const ToViewCell = header ? HeaderColumnNoStick : Cell;
    return (
      <ToViewCell
        key={month}
        colSpan={colSpan}
        className={
          'mc_cell ' +
          (isANumber(percentage)
            ? generatePercentageColor(
                percentage!,
                undefined,
                true
                // baseLine,
                // target
              )
            : '')
        }
      >
        <span>
          {typeof value === 'undefined'
            ? messages.withoutData
            : convertNumbersToProperFormat(value as number, 2)}
        </span>
        {typeof percentage !== 'undefined' && isANumber(percentage) && (
          <Badge
            className={
              generatePercentageColor(
                percentage!,
                undefined,
                true
                // baseLine,
                // target
              ) + ' mc_badgeCell'
            }
          >
            {messages.percentageValue(
              convertNumbersToProperFormat(bound(Number(percentage!))!, 2) // to fix show negative ZERO
            )}
          </Badge>
        )}
      </ToViewCell>
    );
  }

  insertReportToTree = (
    reportGroupRoot: Node,
    itemTags: ITagView[],
    reportId: number,
    groupTags: number[],
    requestEntry: {
      data: IQueryState;
      order: number;
    },
    noAccess: boolean,
    notViewedReportIds: { timeBased: number[]; tagBased: number[] },
    columnType: 'tag' | 'date'
  ) => {
    if (
      !requestEntry.data[0]?.buckets[0] ||
      requestEntry.data[0]?.buckets[0]?.subBuckets?.length ||
      (columnType === 'date' &&
        requestEntry.data[0]?.buckets[0].field !== 'effectiveJMonth')
    ) {
      notViewedReportIds.timeBased.push(reportId);
      return;
    }
    if (
      !requestEntry.data[0]?.buckets[0] ||
      requestEntry.data[0]?.buckets[0]?.subBuckets?.length ||
      (columnType === 'tag' &&
        requestEntry.data[0]?.buckets[0].field !== 'allAnswers')
    ) {
      notViewedReportIds.tagBased.push(reportId);
      return;
    }
    let current = reportGroupRoot;
    groupTags.forEach((tagId) => {
      const foundTag = itemTags.find((tag) => tag.parentId === tagId);
      if (foundTag) {
        let node = current.nodes.find((node) => node.tag!.id === foundTag.id);
        if (!node) {
          node = {
            tag: foundTag,
            nodes: [],
            reports: [],
          };
          current.nodes.push(node);
        }
        current = node;
      }
    });
    current.reports.push({ id: reportId, request: requestEntry, noAccess });
  };

  setChildDepth = (root: Node) => {
    root.childDepth = root.reports.length ? 1 : 0;
    root.height = root.reports.length;
    root.nodes.forEach((child) => {
      this.setChildDepth(child);
      if (child.childDepth! + 1 > root.childDepth!) {
        root.childDepth = child.childDepth! + 1;
        root.headerTitles = [
          this.props.tags.data[root.tag?.parentId ?? 0]?.text || '',
          ...(child.headerTitles || []),
        ];
      }
      root.height! += child.height!;
    });
    root.nodes.sort((a, b) => (a.tag!.order ?? 0) - (b.tag!.order ?? 0));
  };

  setTitleSpan(root: Node, overSpan: number = 0) {
    root.nodes.forEach((child) => {
      this.setTitleSpan(
        child,
        root.childDepth! - child.childDepth! - 1 + overSpan
      );
    });
    root.titleSpan = root.childDepth! + overSpan;
  }

  private renderNode(
    node: Node,
    dates: number[],
    root: boolean,
    columnType: 'date' | 'tag',
    renderGroupAfter: { [key: number]: number }
  ): { node: JSX.Element; totalPercent: number | undefined }[] {
    const ret: {
      item: { node: any; totalPercent: number | undefined };
      index: number;
    }[] = [];
    const head: any = (
      <HeaderColumnNoStick rowSpan={node.height}>
        {node.tag?.text}
      </HeaderColumnNoStick>
    );
    node.nodes.forEach((child, index) => {
      let count = 0;
      let sum = 0;
      let first: any;
      const childNodes = this.renderNode(
        child,
        dates,
        false,
        columnType,
        renderGroupAfter
      );
      childNodes.forEach((it, groupIndex) => {
        const node = { item: it, index };
        if (!groupIndex) first = node;
        if (isANumber(it.totalPercent)) {
          count++;
          sum += bound(it.totalPercent)!;
        }
        ret.push(node);
      });
      // adding group percent
      if (root) {
        const totalPercentageOfGroup = count ? bound(sum / count) : undefined;
        first.item.node = (
          <>
            {first.item.node}
            <HeaderColumnNoStick rowSpan={childNodes.length}>
              {totalPercentageOfGroup
                ? '٪ ' + convertNumbersToProperFormat(totalPercentageOfGroup, 2)
                : messages.withoutData}
            </HeaderColumnNoStick>
          </>
        );
      }
    });
    node.reports.forEach((report, index) => {
      const reportRow = this.renderReport(
        [report.id.toString(), report.request],
        dates,
        1,
        report.noAccess,
        columnType,
        renderGroupAfter
      );
      if (index === 0 && (node.titleSpan || 1) > 1) {
        reportRow.node = (
          <>
            <HeaderColumnNoStick
              rowSpan={node.reports.length}
              colSpan={(node.titleSpan || 1) - 1}
            >
              {messages.withoutData}
              {messages.withoutData}
            </HeaderColumnNoStick>
            {reportRow.node}
          </>
        );
      }
      ret.push({
        item: reportRow,
        index: node.nodes.length,
      });
    });
    if (!root)
      ret[0].item.node = (
        <>
          {head}
          {ret[0].item.node}
        </>
      );
    if (root)
      return ret.map((item, index) => ({
        node: (
          <tr key={index} className={item.index % 2 ? 'alterColor' : ''}>
            {item.item.node}
          </tr>
        ),
        totalPercent: item.item.totalPercent,
      }));
    return ret.map((it) => it.item);
  }
}

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = {
  cumulativePercentageReady: (props: {
    groupId: string;
    key: string;
    percentage: number;
    boundPercentage: number;
  }) => {
    return appDispatch(cumulativePercentageReady(props));
  },
};

export default connect(null, mapDispatchToProps)(MergedChart);
